import React from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { can, canAll } from "@src/security/permissions";

import styles from "./VoterRoll.module.styl";

const formatDate = (value) => value ? String(value).slice(0, 10) : "—";
const formatDateTime = (value) => value ? String(value).replace("T", " ").slice(0, 19) : "—";
const formatNumber = (value) => Number(value || 0).toLocaleString();

const formatStatus = (value) => {
  const status = String(value || "").toLowerCase();
  if (!status) return "—";

  const key = `voterRoll.status.${status}`;
  const translated = t(key);
  return translated === key ? String(value) : translated;
};

const Detail = ({ label, value }) => (
  <div className={styles.detailItem}>
    <div className={styles.detailLabel}>{label}</div>
    <div className={styles.detailValue}>{value}</div>
  </div>
);

const VoterRollDetails = ({ gui, id, expectedType }) => {
  const [roll, setRoll] = React.useState(null);
  const [electoralEvent, setElectoralEvent] = React.useState(null);
  const [entryCount, setEntryCount] = React.useState(null);
  const [amendmentCount, setAmendmentCount] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const currentRoll = await rest.request(
          getServiceUri() + `voter-roll/${Number(id)}`,
          "GET"
        );

        if (cancelled) {
          return;
        }

        if (
          expectedType &&
          String(currentRoll?.typeCode || "").toUpperCase() !== expectedType
        ) {
          throw new Error(t("voterRoll.typeMismatch"));
        }

        setRoll(currentRoll);

        const eventId = Number(currentRoll?.electoralEventId);

        if (Number.isFinite(eventId) && can(gui, "READ_ELECTORAL_EVENT")) {
          try {
            const event = await rest.request(
              getServiceUri() + `electoral-event/${eventId}`,
              "GET"
            );
            if (!cancelled) {
              setElectoralEvent(event);
            }
          } catch (_) {
            if (!cancelled) {
              setElectoralEvent(null);
            }
          }
        }

        const countPayload = {
          columns: [],
          query: `voterRollId==${Number(id)}`,
          limit: null,
          offset: null
        };

        if (can(gui, "COUNT_VOTER_ROLL_ENTRY")) {
          try {
            const count = await rest.request(
              getServiceUri() + "voter-roll-entry/count",
            "POST",
            countPayload
          );
          if (!cancelled) {
            setEntryCount(Number(count || 0));
          }
        } catch (_) {
            if (!cancelled) {
              setEntryCount(null);
            }
          }
        }

        if (can(gui, "COUNT_VOTER_ROLL_AMENDMENT")) {
          try {
            const count = await rest.request(
              getServiceUri() + "voter-roll-amendment/count",
            "POST",
            countPayload
          );
          if (!cancelled) {
            setAmendmentCount(Number(count || 0));
          }
        } catch (_) {
            if (!cancelled) {
              setAmendmentCount(null);
            }
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || String(e));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [gui, id, expectedType]);

  const routeSegment = expectedType === "FINAL" ? "final" : "preliminary";

  if (loading) {
    return <div className={styles.loading}>{t("common.loading")}</div>;
  }

  if (error || !roll) {
    return <div className={styles.error}>{error || t("voterRoll.notFound")}</div>;
  }

  return (
    <div className={styles.page}>
      {canAll(gui, ["SEARCH_VOTER_ROLL", "COUNT_VOTER_ROLL"]) ? (
        <button
          type="button"
          className={styles.backButton}
          onClick={() => gui.goTo(`/voter-roll/${routeSegment}`)}
        >
          <ArrowBackIcon fontSize="small" />
          {expectedType === "FINAL"
            ? t("voterRoll.finalRolls")
            : t("voterRoll.preliminaryRolls")}
        </button>
      ) : null}

      <header className={styles.hero}>
        <div>
          <div className={styles.eyebrow}>
            {expectedType === "FINAL"
              ? t("voterRoll.final")
              : t("voterRoll.preliminary")}
          </div>
          <h1 className={styles.title}>
            {electoralEvent?.name || `${t("voterRoll.roll")} #${roll.id}`}
          </h1>
          <div className={styles.subtitle}>
            {electoralEvent?.eventDate ? formatDate(electoralEvent.eventDate) : ""}
          </div>
        </div>

        <span className={styles.statusBadge}>
          {formatStatus(roll.statusCode)}
        </span>
      </header>

      <section className={styles.summaryGrid}>
        <Detail label={t("voterRoll.event")} value={electoralEvent?.name || `#${roll.electoralEventId}`} />
        <Detail label={t("voterRoll.eventDate")} value={formatDate(electoralEvent?.eventDate)} />
        <Detail label={t("voterRoll.cutoff")} value={formatDateTime(roll.cutoffAt)} />
        <Detail label={t("voterRoll.createdAt")} value={formatDateTime(roll.createdAt)} />
        <Detail label={t("voterRoll.closedAt")} value={formatDateTime(roll.closedAt)} />
        <Detail label={t("voterRoll.entries")} value={entryCount === null ? "—" : formatNumber(entryCount)} />
        <Detail label={t("voterRoll.amendments")} value={amendmentCount === null ? "—" : formatNumber(amendmentCount)} />
        {roll.sourceRollId ? (
          <Detail label={t("voterRoll.sourceRoll")} value={`#${roll.sourceRollId}`} />
        ) : null}
      </section>

    </div>
  );
};

export const displayPreliminaryRoll = (gui, id) => ({
  uuid: `preliminary-roll-${id}`,
  view: () => (
    <VoterRollDetails
      gui={gui}
      id={id}
      expectedType="PRELIMINARY"
    />
  )
});

export const displayFinalRoll = (gui, id) => ({
  uuid: `final-roll-${id}`,
  view: () => (
    <VoterRollDetails
      gui={gui}
      id={id}
      expectedType="FINAL"
    />
  )
});

export default VoterRollDetails;
